"use client";

import { safeGetItem, safeSetItem } from "@/lib/storage/safe-storage";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { LayoutDashboardIcon, X } from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import { useEntitlementRevalidate } from "@/lib/billing/use-entitlement-revalidate";
import { signOutCurrentDevice } from "@/lib/auth/logout";
import { redirectToBilling } from "@/lib/intentional-navigation";
import { isCustomCheckoutEnabled } from "@/lib/billing/checkout-mode";
import { PandaBrandLogo } from "@/components/panda-brand-logo";
import { PandaLogo } from "@/components/panda-logo";
import { HomeUserAccountMenu } from "@/components/home-user-account-menu";
import { FooterThemeToggle } from "@/components/footer-theme-toggle";
import { MobileNavLinks } from "@/components/mobile-nav-links";
import { SignupModal } from "@/components/auth/signup-modal";
import { LoginModal } from "@/components/auth/login-modal";
import { PricingSection } from "@/components/pricing/pricing-section";
import { PricingTiersSection } from "@/components/pricing/pricing-tiers-section";
import {
  centsToDollars,
  tierPerMonthCents,
  PRICING_TIERS,
  type PaidTierId,
  type BillingCadence,
} from "@/lib/pricing/credit-tiers";
import { interceptIfPaymentsPaused } from "@/components/billing/payments-paused-store";
import {
  completedOnSavedCard,
  type PurchaseResponse,
} from "@/lib/billing/direct-charge-client";
import { resolvePurchaseReturnPath } from "@/lib/billing/purchase-return-path";
import { useAuthModalSync } from "@/lib/pending-auth";
import { trackPricingPageViewed, trackCheckoutStarted } from "@/lib/analytics";
import { readCadencePreference } from "@/lib/billing/cadence-preference";
import {
  handleStartFreeClick,
  type StartFreeSource,
} from "@/lib/marketing/start-free";
import { toast } from "sonner";
import { TRIAL_CONVERT_EVENT } from "@/lib/trial/upgrade-entry-client";

// New ticket pricing page (Free/Pro/Grow/Scale, credit dropdowns). Flag-gated
// OFF so the current pricing page stays live until the prices are confirmed and
// checkout is wired. Flip NEXT_PUBLIC_NEW_PRICING_PAGE=1 to preview/enable.
const NEW_PRICING_PAGE = process.env.NEXT_PUBLIC_NEW_PRICING_PAGE === "1";

type PageUser = {
  email?: string;
  userMetadata?: Record<string, unknown> | null;
} | null;

export default function PricingPage() {
  const router = useRouter();
  const [user, setUser] = useState<PageUser>(null);
  const [userPlan, setUserPlan] = useState<string>("free");
  // Active $1 trial. Read from the SAME /api/credits payload as `plan`, which
  // reads 'pro' for these accounts — so the Pro card locks as "Current plan"
  // and every other tier offered a live purchase that trialPurchaseGuard 403s
  // for the whole trial. Four out-of-credits toasts (home, templates, template
  // detail, community) navigate here, so this was the landing page for exactly
  // the population the trial-routing work is about.
  const [userTrial, setUserTrial] = useState(false);
  // Has /api/credits answered? `userPlan` defaults to "free", which is not a
  // fact about the account — it is the value held until the read lands — and
  // every card read it as one. An active Pro user got a live "Upgrade" CTA on
  // Pro for the first seconds of the page, and clicking it inside that window
  // opened a checkout for a second subscription.
  //
  // Fails OPEN, unlike the builder's equivalent gate: /api/billing/subscribe-tiered
  // now refuses a true duplicate itself (lib/billing/duplicate-subscription-guard.ts),
  // so a credits read that errors must not be able to take the buy buttons off
  // the pricing page. The server is the integrity guard; this only stops the
  // user wasting the click.
  const [planResolved, setPlanResolved] = useState(false);
  const [hasProject, setHasProject] = useState(false);
  const pageViewedRef = useRef(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [signupNext, setSignupNext] = useState<string>("/pricing");
  // Pre-fill for the signup modal's email step, set only by the login modal's
  // "No account found — Sign up" handoff. Cleared on close so it never leaks
  // into a later, unrelated signup open.
  const [signupInitialEmail, setSignupInitialEmail] = useState<string>("");
  // Which surface opened the signup modal, for funnel analytics. Set by
  // openSignup and read by <SignupModal source=…>.
  const [signupSource, setSignupSource] = useState<string>("unspecified");
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginNext, setLoginNext] = useState<string>("/pricing");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Signed-in tier selection. With a card on file the server charges it and
  // activates the plan without ever leaving Panda; otherwise it returns a Stripe
  // Checkout URL (server computes/verifies the price either way). Anonymous
  // users never reach this (PricingTiersSection routes them to login first).
  async function handleChoosePlan(
    tier: PaidTierId,
    credits: number,
    cadence: BillingCadence,
  ) {
    // A trialing account cannot complete ANY of these: every plan route is
    // 403'd until the trial converts (lib/trial/purchase-guard.ts). Handed to
    // the app-wide convert host — mounted in the root layout, so it is
    // listening here too — which is the one purchase this user can actually
    // finish. Checked before trackCheckoutStarted so a click that never starts
    // a checkout is not reported as one.
    // Where this buyer came from, resolved at CLICK time rather than on mount
    // (the recording is written on navigation). Every branch below carries it,
    // including the conversion — which used to be the one that hardcoded
    // Settings → Billing and so ended every pricing-page purchase there.
    const returnTo = resolvePurchaseReturnPath();
    if (userTrial) {
      window.dispatchEvent(
        new CustomEvent(TRIAL_CONVERT_EVENT, {
          detail: {
            entrypoint: "pricing_page_cta",
            entrySurface: "pricing",
            returnTo,
          },
        }),
      );
      return;
    }
    // Before the request, so it covers a saved-card charge that never reaches
    // Stripe as well as the redirect path.
    trackCheckoutStarted({ plan: tier, cadence, surface: "pricing_page" });
    // Payments-paused guard. After trackCheckoutStarted so the click still
    // counts as intent in the pricing funnel — the buttons stay live precisely
    // so a blocked outage does not also blank out the demand signal.
    if (
      interceptIfPaymentsPaused({
        kind: "upgrade",
        surface: "pricing_page",
        label: `${PRICING_TIERS[tier]?.name ?? tier} — ${credits.toLocaleString()} credits`,
        priceLabel: `${centsToDollars(tierPerMonthCents(tier, credits, cadence))}/mo`,
        plan: tier,
        cadence,
        credits,
        amountUsd: tierPerMonthCents(tier, credits, cadence) / 100,
      })
    ) {
      return;
    }
    // Custom Payment Element checkout (behind the flag): go to our own /checkout
    // page. The saved-card fast path still runs there (the route charges it
    // server-side and returns { charged:true } before any Elements render).
    if (isCustomCheckoutEnabled()) {
      redirectToBilling(
        `/checkout?type=tiered&tier=${encodeURIComponent(tier)}&credits=${credits}&cadence=${encodeURIComponent(cadence)}&returnTo=${encodeURIComponent(returnTo)}`,
      );
      return;
    }
    try {
      const res = await fetch("/api/billing/subscribe-tiered", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // The affordance that sold it, declared by the surface that owns it
        // (PANDA-2310). The route stamps this into provider metadata, so it
        // reaches the checkout event AND the purchase event — previously both
        // reported this sale as coming from settings, which no /pricing buyer
        // ever did.
        body: JSON.stringify({
          tier,
          credits,
          cadence,
          returnTo,
          entrypoint: "pricing_page_tier_cta",
          surface: "pricing",
        }),
      });
      const data = (await res.json().catch(() => null)) as PurchaseResponse | null;
      if (res.ok && completedOnSavedCard(data, returnTo)) return;
      if (!res.ok || !data?.url) {
        toast.error(data?.error ?? "Couldn't start checkout. Please try again.");
        return;
      }
      redirectToBilling(data.url);
    } catch {
      toast.error("Couldn't start checkout. Please try again.");
    }
  }

  function openSignup(
    nextPath: string = "/pricing",
    email: string = "",
    source: string = "unspecified",
  ) {
    setSignupNext(nextPath);
    setSignupInitialEmail(email);
    setSignupSource(source);
    setSignupOpen(true);
  }

  // Every free-tier CTA on this page (nav pill, mobile bar, mobile menu, the
  // Free plan card) routes through here. Signed-out visitors get signup;
  // signed-in visitors go to their projects. See lib/marketing/start-free.ts.
  //
  // These used to `router.push("/?focus=prompt")`, which bounced the visitor
  // to the homepage and — because that handler targeted a DOM id the live hero
  // no longer uses — focused nothing at all. `next` is "/" rather than
  // "/pricing" so a completed signup lands on the build surface, not back on
  // the price list they'd just accepted.
  function startFree(source: StartFreeSource) {
    handleStartFreeClick({
      source,
      signedIn: !!user,
      openSignup: (nextPath) => openSignup(nextPath, "", source),
      navigate: (href) => router.push(href),
    });
  }

  function openLogin(nextPath: string = "/pricing") {
    setLoginNext(nextPath);
    setLoginOpen(true);
  }

  useAuthModalSync(loginOpen, signupOpen, setLoginOpen, setSignupOpen);

  // Capture ?ref= for referral attribution (defensive — also captured on the
  // homepage at app/home-client.tsx). The Remix with Panda badge now routes to the
  // homepage, but any direct /pricing?ref=<id> link still stores the ref in the
  // panda_ref cookie here, which the signup modal reads to credit the referrer.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const refParam = new URLSearchParams(window.location.search).get("ref");
    if (refParam && !safeGetItem("panda_ref")) {
      safeSetItem("panda_ref", refParam);
      document.cookie = `panda_ref=${refParam}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`;
    }
  }, []);

  // Fire pricing_page_viewed exactly once, with accurate auth + plan
  // context — for signed-in users that means waiting for /api/credits.
  const firePageViewed = useCallback((isLoggedIn: boolean, plan: string) => {
    if (pageViewedRef.current) return;
    pageViewedRef.current = true;
    trackPricingPageViewed({
      is_logged_in: isLoggedIn,
      current_plan: plan,
      surface: "pricing_page",
      // The interval the page actually OPENED on — the persisted preference
      // for a returning visitor, the product default for a new one. Read
      // rather than defaulted so the annual-mix reading can tell "was shown
      // annual" apart from "switched to annual".
      default_cadence: readCadencePreference(),
    });
  }, []);

  // Load the active plan so the matching pricing card locks ("Current plan")
  // and can't be re-purchased, plus whether the user already has a project
  // (drives the bottom CTA route). Resets on sign-out.
  //
  // Lifted out of the mount effect so a purchase settling elsewhere can call it
  // again: this page reads the plan ONCE, so buying Pro from the header chip
  // while standing on it left every card unlocked and the Pro card still
  // offering itself for sale.
  const loadPlan = useCallback(() => {
    fetch("/api/credits")
      .then((r) => r.json())
      .then((creds) => {
        if (creds?.error) {
          // We asked and could not be told. Release the buttons rather than
          // stranding a buyer behind a failed read — the server refuses the
          // one request this gate exists to prevent.
          setPlanResolved(true);
          firePageViewed(true, "free");
          return;
        }
        const plan = String(creds.plan ?? "free");
        setUserPlan(plan);
        setUserTrial(creds.trial === true);
        setHasProject((Number(creds.total_projects_created) || 0) > 0);
        setPlanResolved(true);
        firePageViewed(true, plan);
      })
      .catch(() => {
        setPlanResolved(true);
        firePageViewed(true, "free");
      });
  }, [firePageViewed]);

  useEntitlementRevalidate(loadPlan);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data }) => {
      if (!data.user) {
        // Nobody to resolve a plan for. A signed-out visitor's CTA routes to
        // signup, not to a checkout, so there is nothing to hold it for.
        setPlanResolved(true);
        firePageViewed(false, "free");
        return;
      }
      setUser({
        email: data.user.email,
        userMetadata: (data.user.user_metadata ?? null) as Record<
          string,
          unknown
        > | null,
      });
      loadPlan();
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user) {
        setUser(null);
        setUserPlan("free");
        setHasProject(false);
        return;
      }
      setUser({
        email: session.user.email,
        userMetadata: (session.user.user_metadata ?? null) as Record<
          string,
          unknown
        > | null,
      });
      loadPlan();
    });
    return () => sub.subscription.unsubscribe();
  }, [firePageViewed, loadPlan]);

  // Lock page scroll + close on Escape while the mobile full-screen menu is
  // open, matching the homepage / AppHeader overlays so close behavior is
  // identical across pages.
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMobileMenuOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = original;
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileMenuOpen]);

  async function handleLogout() {
    const supabase = createClient();
    await signOutCurrentDevice(supabase);
    setUser(null);
    router.refresh();
  }

  // New pricing page (flag-gated). Self-contained layout that mirrors the
  // mockup's navbar + footer (Templates · Pricing · Log in · Build free /
  // Templates · Pricing · Privacy · Terms) using OUR logo + colors. Kept as an
  // early return so the current pricing page below stays untouched until launch.
  if (NEW_PRICING_PAGE) {
    const navLink =
      "rounded-md px-3 py-2 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-foreground/85 transition-colors hover:text-foreground";
    const buildFreeBtn =
      "inline-flex items-center justify-center rounded-full bg-panda-gradient-success px-6 py-2.5 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-white shadow-md shadow-emerald-900/30 transition-all hover:shadow-lg hover:brightness-110";
    return (
      <div className="min-h-screen bg-background text-foreground">
        <nav className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
          <div className="mx-auto flex w-full max-w-[1264px] items-center justify-between gap-4 px-4 py-3 sm:px-8">
            <Link href="/" aria-label="Panda home" className="shrink-0 text-foreground transition-opacity hover:opacity-90">
              <PandaBrandLogo />
            </Link>
            <div className="hidden items-center gap-2 md:flex lg:gap-4">
              <Link href="/templates" className={navLink}>Templates</Link>
              <Link
                href="/pricing"
                className="rounded-md px-3 py-2 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-emerald-700 transition-colors hover:text-foreground dark:text-emerald-400"
              >
                Pricing
              </Link>
              {user && (
                <Link href="/projects" className={navLink}>Projects</Link>
              )}
              <FooterThemeToggle className="hidden sm:inline-flex" />
              {user ? (
                <HomeUserAccountMenu
                  email={user.email}
                  userMetadata={user.userMetadata}
                  onLogout={handleLogout}
                />
              ) : (
                <button type="button" onClick={() => openLogin("/pricing")} className={navLink}>
                  Log in
                </button>
              )}
              <button type="button" onClick={() => startFree("pricing_nav")} className={buildFreeBtn}>
                Build free →
              </button>
            </div>
            {/* Mobile */}
            <div className="flex items-center gap-2 md:hidden">
              <button type="button" onClick={() => startFree("pricing_nav_mobile")} className="inline-flex items-center justify-center rounded-full bg-panda-gradient-success px-4 py-2 text-sm font-medium text-white">
                Build free →
              </button>
              <button
                type="button"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                onClick={() => setMobileMenuOpen((v) => !v)}
                className="flex h-10 w-10 items-center justify-center text-foreground"
              >
                <X size={mobileMenuOpen ? 18 : 0} className={mobileMenuOpen ? "" : "hidden"} />
                {!mobileMenuOpen && (
                  <svg width="18" height="9" viewBox="0 0 18 9" fill="none" aria-hidden>
                    <path d="M16.75 7C17.1642 7 17.5 7.33579 17.5 7.75C17.5 8.16421 17.1642 8.5 16.75 8.5H0.75C0.335786 8.5 0 8.16421 0 7.75C0 7.33579 0.335786 7 0.75 7H16.75ZM16.75 0C17.1642 0 17.5 0.335787 17.5 0.75C17.5 1.16421 17.1642 1.5 16.75 1.5H0.75C0.335786 1.5 0 1.16421 0 0.75C0 0.335786 0.335786 0 0.75 0H16.75Z" fill="currentColor" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </nav>

        {mobileMenuOpen && (
          <div role="dialog" aria-modal="true" aria-label="Main menu" className="fixed inset-0 z-[70] flex flex-col bg-background md:hidden">
            <div className="flex h-14 items-center justify-between px-4">
              <FooterThemeToggle />
              <button type="button" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)} className="flex h-6 w-6 items-center justify-center text-foreground">
                <X size={24} strokeWidth={2} />
              </button>
            </div>
            {/* Log in sits inside MobileNavLinks (directly under Home) for
                signed-out visitors. No "Start free" row: the mobile bar
                already renders "Build free →" beside the burger, so one here
                put two identical signup CTAs on screen at once. */}
            <MobileNavLinks
              signedIn={!!user}
              activeKey="pricing"
              onNavigate={() => setMobileMenuOpen(false)}
              onLogin={() => openLogin("/pricing")}
            />
          </div>
        )}

        <main>
          <PricingTiersSection
            user={user}
            openLogin={openLogin}
            postAuthPath="/pricing"
            currentPlan={userPlan}
            planResolved={planResolved}
            onStartForFree={() => startFree("pricing_free_tier")}
            onChoosePlan={handleChoosePlan}
          />
        </main>

        <footer className="border-t border-border bg-[#F8FAFC] py-8 dark:bg-card">
          <div className="mx-auto flex max-w-[1264px] flex-col items-start justify-between gap-4 px-4 sm:flex-row sm:items-center sm:px-8">
            <Link href="/" className="flex items-center gap-2 text-foreground transition-opacity hover:opacity-80" aria-label="Panda home">
              <PandaLogo size="xs" className="rounded-md" />
              <span className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium text-foreground">panda</span>
            </Link>
            <nav className="flex flex-wrap items-center gap-4 sm:gap-6">
              <Link href="/templates" className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium text-foreground/85 transition-colors hover:text-foreground">Templates</Link>
              <Link href="/pricing" className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium text-foreground/85 transition-colors hover:text-foreground">Pricing</Link>
              <Link href="/privacy" className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal text-[#94A3B8] transition-colors hover:text-foreground">Privacy</Link>
              <Link href="/terms" className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal text-[#94A3B8] transition-colors hover:text-foreground">Terms</Link>
              <span className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal text-[#94A3B8]">© {new Date().getFullYear()} Panda</span>
            </nav>
          </div>
        </footer>

        <SignupModal
          open={signupOpen}
          onOpenChange={(o) => {
            setSignupOpen(o);
            if (!o) setSignupInitialEmail("");
          }}
          next={signupNext}
          initialEmail={signupInitialEmail}
          source={signupSource}
        />
        <LoginModal
          open={loginOpen}
          onOpenChange={setLoginOpen}
          next={loginNext}
          onSwitchToSignup={(email) => {
            setLoginOpen(false);
            openSignup(loginNext, email, "login_modal_switch");
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <nav className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto grid w-full max-w-[1920px] grid-cols-[auto_1fr_auto] items-center gap-4 px-4 py-3 sm:px-8 lg:px-12">
          <Link
            href="/"
            className="shrink-0 text-foreground transition-opacity hover:opacity-90"
            aria-label="Panda home"
          >
            <PandaBrandLogo />
          </Link>
          <div className="hidden items-center justify-center gap-2 md:flex lg:gap-4">
            <Link
              href="/pricing"
              className="rounded-md px-3 py-2 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-emerald-700 transition-colors hover:text-foreground dark:text-emerald-400"
            >
              Pricing
            </Link>
            <Link
              href="/templates"
              className="rounded-md px-3 py-2 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-foreground/85 transition-colors hover:text-foreground"
            >
              Templates
            </Link>
            {user && (
              <Link
                href="/projects"
                className="rounded-md px-3 py-2 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-foreground/85 transition-colors hover:text-foreground"
              >
                Projects
              </Link>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 sm:gap-5">
            <div className="md:hidden">
              <Link href="/">
                <LayoutDashboardIcon />
              </Link>
            </div>
            {user ? (
              <>
                <FooterThemeToggle className="hidden sm:inline-flex" />
                <HomeUserAccountMenu
                  email={user.email}
                  userMetadata={user.userMetadata}
                  onLogout={handleLogout}
                />
              </>
            ) : (
              <>
                <FooterThemeToggle className="hidden sm:inline-flex" />
                <button
                  type="button"
                  onClick={() => openLogin("/pricing")}
                  className="hidden sm:inline-flex font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-foreground/85 transition-colors hover:text-foreground"
                >
                  Log in
                </button>
                <button
                  type="button"
                  onClick={() => openSignup("/pricing", "", "pricing_nav_signup")}
                  className="hidden sm:inline-flex items-center justify-center rounded-full bg-panda-gradient-success px-6 py-2.5 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-white shadow-md shadow-emerald-900/30 transition-all hover:shadow-lg hover:brightness-110"
                >
                  Sign up
                </button>
              </>
            )}
            <button
              type="button"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="-mr-1 flex h-10 w-10 shrink-0 items-center justify-center text-foreground transition-opacity hover:opacity-70 md:hidden"
            >
              {mobileMenuOpen ? (
                <X size={18} />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="9"
                  viewBox="0 0 18 9"
                  fill="none"
                  aria-hidden
                >
                  <path
                    d="M16.75 7C17.1642 7 17.5 7.33579 17.5 7.75C17.5 8.16421 17.1642 8.5 16.75 8.5H0.75C0.335786 8.5 -3.21882e-08 8.16421 0 7.75C3.21882e-08 7.33579 0.335786 7 0.75 7H16.75ZM16.75 0C17.1642 4.07381e-08 17.5 0.335787 17.5 0.75C17.5 1.16421 17.1642 1.5 16.75 1.5H0.75C0.335786 1.5 -3.21882e-08 1.16421 0 0.75C3.21882e-08 0.335786 0.335786 -4.07382e-08 0.75 0H16.75Z"
                    fill="currentColor"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Main menu"
          className="fixed inset-0 z-[70] flex flex-col bg-background md:hidden"
        >
          <div className="flex h-14 items-center justify-between px-4">
            <FooterThemeToggle />
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setMobileMenuOpen(false)}
              className="flex h-6 w-6 items-center justify-center text-foreground transition-opacity hover:opacity-70"
            >
              <X size={24} strokeWidth={2} />
            </button>
          </div>
          {/* Log in is a nav row (directly under Home) via MobileNavLinks —
              the bottom area keeps only the primary Sign up CTA. */}
          <MobileNavLinks
            signedIn={!!user}
            activeKey="pricing"
            onNavigate={() => setMobileMenuOpen(false)}
            onLogin={() => openLogin("/pricing")}
          />
          {!user && (
            <div className="mt-auto flex flex-col gap-3 px-4 pb-6">
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  openSignup("/pricing", "", "pricing_nav_signup");
                }}
                className="flex h-12 items-center justify-center rounded-full bg-panda-gradient-success font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-white shadow-md shadow-emerald-900/30 transition-all hover:shadow-lg hover:brightness-110"
              >
                Sign up
              </button>
            </div>
          )}
        </div>
      )}

      <main>
        {NEW_PRICING_PAGE ? (
          <PricingTiersSection
            user={user}
            openLogin={openLogin}
            postAuthPath="/pricing"
            currentPlan={userPlan}
            planResolved={planResolved}
            onStartForFree={() => startFree("pricing_free_tier")}
            onChoosePlan={handleChoosePlan}
          />
        ) : (
          <PricingSection
            user={user}
            openLogin={openLogin}
            postAuthPath="/pricing"
            showTryFreeCard
            currentPlan={userPlan}
            planResolved={planResolved}
            hasProject={hasProject}
            // "Get started for free" (logged-out) → open signup right here.
            // "Start building" (signed-in free, no project) → into the app.
            // Both used to bounce to the homepage prompt box, which lost the
            // logged-out visitor at exactly the moment they'd shown intent.
            onStartForFree={() => startFree("pricing_free_tier")}
          />
        )}
      </main>

      <footer className="bg-[#F8FAFC] pb-20 dark:bg-card lg:bg-linear-to-r lg:from-[#f5f1f7] lg:via-[#f7eef1] lg:to-[#faece6] lg:py-6 lg:dark:from-card lg:dark:via-card lg:dark:to-card">
        <div className="mx-auto hidden max-w-7xl px-4 pr-20 sm:px-8 sm:pr-24 lg:flex lg:max-w-[90rem] lg:flex-row lg:items-center lg:justify-between lg:gap-8 lg:px-12 lg:pr-24">
          <div className="flex flex-wrap items-center justify-center gap-5 lg:justify-start lg:gap-8">
            <Link
              href="/"
              className="flex items-center gap-2 text-foreground transition-opacity hover:opacity-80"
              aria-label="Panda home"
            >
              <PandaLogo size="xs" className="rounded-md" />
              <span className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium leading-5 text-foreground">
                panda
              </span>
            </Link>
            <nav className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              <Link
                href="/pricing"
                className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium leading-5 text-foreground/85 transition-colors hover:text-foreground"
              >
                Pricing
              </Link>
              <Link
                href="/templates"
                className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium leading-5 text-foreground/85 transition-colors hover:text-foreground"
              >
                Templates
              </Link>
            </nav>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-5 lg:justify-end">
            <Link
              href="/terms"
              className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#94A3B8] transition-colors hover:text-foreground"
            >
              Terms &amp; Conditions
            </Link>
            <Link
              href="/privacy"
              className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#94A3B8] transition-colors hover:text-foreground"
            >
              Privacy Policy
            </Link>
            <span className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#94A3B8]">
              &copy; {new Date().getFullYear()} Panda
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-6 p-4 lg:hidden">
          <div className="flex items-start justify-between">
            <div className="flex flex-col items-start justify-center gap-3">
              <Link
                href="/"
                className="flex items-center gap-2 text-foreground transition-opacity hover:opacity-80"
                aria-label="Panda home"
              >
                <PandaLogo size="xs" className="rounded-md" />
                <span className="font-[family-name:var(--font-rubik),sans-serif] text-2xl font-bold leading-none tracking-tight text-foreground">
                  panda
                </span>
              </Link>
              <nav className="flex flex-col items-start gap-1">
                <Link
                  href="/pricing"
                  className="flex h-7 items-center font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-foreground transition-colors hover:text-foreground"
                >
                  Pricing
                </Link>
                <Link
                  href="/templates"
                  className="flex h-7 items-center font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-foreground transition-colors hover:text-foreground"
                >
                  Templates
                </Link>
              </nav>
            </div>
            <FooterThemeToggle />
          </div>
          <div className="flex items-center gap-4">
            <Link
              href="/terms"
              className="font-[family-name:var(--font-rubik),sans-serif] text-xs font-normal leading-4 text-[#77869D] transition-colors hover:text-foreground"
            >
              Terms &amp; Conditions
            </Link>
            <Link
              href="/privacy"
              className="font-[family-name:var(--font-rubik),sans-serif] text-xs font-normal leading-4 text-[#77869D] transition-colors hover:text-foreground"
            >
              Privacy Policy
            </Link>
            <span className="font-[family-name:var(--font-rubik),sans-serif] text-xs font-normal leading-4 text-[#77869D]">
              &copy; {new Date().getFullYear()} Panda
            </span>
          </div>
        </div>
      </footer>

      <SignupModal
        open={signupOpen}
        onOpenChange={(o) => {
          setSignupOpen(o);
          if (!o) setSignupInitialEmail("");
        }}
        next={signupNext}
        initialEmail={signupInitialEmail}
        source={signupSource}
      />
      <LoginModal
        open={loginOpen}
        onOpenChange={setLoginOpen}
        next={loginNext}
        onSwitchToSignup={(email) => {
          setLoginOpen(false);
          openSignup(loginNext, email, "login_modal_switch");
        }}
      />
    </div>
  );
}
